'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/prob-extra'

const ExpectedValueGame = dynamic(() => import('../_components/widgets/ProbExtraWidgets').then(m => m.ExpectedValueGame), { ssr: false })

export default function ExpectedValue() {
  return (
    <Lesson>
      <Step>
        <H>What happens on average</H>
        <P>
          One roll of a die could be anything from 1 to 6. But roll it ten thousand times and the average settles down to
          something very predictable. That long-run average is the <strong>expected value</strong>. Work it out by weighting
          each outcome by its probability:
        </P>
        <MB>{'\\mathbb{E}[X] = \\sum_i p_i\\,x_i'}</MB>
        <Figure n="5.7" wide caption="Play a game over and over. The running average wobbles, then locks onto the expected value (dashed). This is the law of large numbers.">
          <ExpectedValueGame />
        </Figure>
        <Callout kind="notation">
          <M>{'\\mathbb{E}'}</M> (a blackboard-bold E) means “expected value of”. <M>X</M> is a <strong>random variable</strong>:
          a number whose value depends on chance, like the result of a roll.
        </Callout>
      </Step>

      <Step gate={['nq-ev', 'q-lottery']}>
        <H>Work it out</H>
        <NumberQuiz
          id="nq-ev"
          question="A game pays 10 with probability 0.2, and 0 otherwise. What is its expected value?"
          answer={2}
          explain="0.2 × 10 + 0.8 × 0 = 2. You’d never actually win exactly 2, but that’s the average per play."
          hint="Multiply each payout by its probability and add."
        />
        <Quiz
          id="q-lottery"
          question="The lottery ticket above has an expected value of −1. Does that mean you’ll lose exactly 1 every time?"
          options={[
            { label: 'No — usually you lose 2, occasionally you win big; it averages −1', correct: true },
            { label: 'Yes', why: 'No single play loses exactly 1. The expected value is an average over many plays.' },
            { label: 'No — you’re likely to come out ahead', why: '99% of the time you lose your 2. Over many plays you lose about 1 per ticket.' },
          ]}
        />
      </Step>

      <Step gate="expectation">
        <H>Spread, and estimating by sampling</H>
        <P>
          Two games can have the same expected value but feel very different: one pays a steady 1, the other pays 100 once in
          100 tries. <strong>Variance</strong> captures that. It’s the expected squared distance from the average, the same idea
          as the spread of data in the last lesson.
        </P>
        <MB>{'\\text{Var}[X] = \\mathbb{E}\\big[(X - \\mathbb{E}[X])^2\\big]'}</MB>
        <P>
          When you can’t list every outcome, just <strong>sample</strong>: run the random process many times and average.
        </P>
        <Exercise
          id="expectation"
          title="expectedValue, variance, estimate"
          {...EX.expectation}
          hints={[
            <>
              <C>{'total += probs[i] * values[i]'}</C> for every outcome.
            </>,
            <>
              variance: <C>{'expectedValue(values.map(x => (x - mu) ** 2), probs)'}</C>
            </>,
            <>
              estimate: add up <C>sample()</C> n times, divide by n.
            </>,
          ]}
        />
      </Step>

      <Step gate="decide">
        <H>Deciding under uncertainty</H>
        <P>
          Expected value tells you what to <em>do</em>. Write down how good each action would be in each possible world, weight
          by how likely each world is, and pick the action with the best average.
        </P>
        <Exercise
          id="decide"
          title="bestAction"
          {...EX.decide}
          hints={[
            <>
              For each action (row): <C>{'row.reduce((s, r, i) => s + r * probs[i], 0)'}</C>. Then <C>vec.argmax</C> of those.
            </>,
          ]}
        />
        <Callout kind="why">
          This is the core of reinforcement learning (Module 10): an agent learns to pick actions that maximise its{' '}
          <em>expected</em> future reward. And the loss you train every model on is an expected value too: the average loss
          over examples drawn from the data.
        </Callout>
      </Step>
    </Lesson>
  )
}
